import type { MedicationStatementDto } from "@repo/data-store";
import type { DoseFrequency, MedicationRoute } from "@repo/domain";
import { type FocusEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import type { RxStrengthOption } from "@/shared/hooks/use-rxnorm-search";
import { dosageSentence, FREQUENCY_OPTIONS } from "@/shared/lib/medication-dosage";
import { DatePill } from "../date-pill";
import { PILL_CLASS, PillCombobox, type PillOption, pillStateClass } from "../pill-combobox";
import { useDrugSearch } from "./drug-search";
import {
  doseLabel,
  FREQUENCY_CHOICES,
  medicationName,
  proseDrugName,
  routeFromForm,
  stopDateInput,
  stopDateLabel,
} from "./medication-sentence";

/** A drug the clinician picked, or typed and kept. */
export type PickedDrug =
  | { kind: "coded"; name: string; route?: MedicationRoute; strengths: readonly RxStrengthOption[] }
  | { kind: "typed"; name: string };

/** Everything the blanks hold, as typed. Turned into a record by the save. */
export interface MedicationAnswers {
  drug: PickedDrug | null;
  /** Null: no strength picked, or "strength unknown". */
  strength: RxStrengthOption | null;
  dose: string;
  /** How often, with "prn" / "as needed" anywhere in it: "bid prn". */
  frequency: string;
  /** Free words added to the dose: "take with food". */
  instructions: string;
  /** When it was stopped, MM/DD/YYYY. Empty means the patient still takes it. */
  stopped: string;
}

/** Stopped is answered by the stop blank alone, so the sentence's verb is never a blank of its own. */
export const isStopped = (answers: Pick<MedicationAnswers, "stopped">) => answers.stopped.trim() !== "";

export function answersFromRecord(record: MedicationStatementDto | undefined): MedicationAnswers {
  const frequency = FREQUENCY_OPTIONS.find((option) => option.value === record?.dosage?.frequency);
  const asNeeded = record?.dosage?.asNeeded ? "as needed" : "";
  // The dose text is the sentence of the structured dose, then the instructions after a comma.
  const sentence = dosageSentence(record?.dosage);
  const text = record?.dosageText ?? "";
  const instructions = sentence && text.startsWith(`${sentence}, `) ? text.slice(sentence.length + 2) : "";
  // Text that is not the sentence of the structured dose is the dose itself, as typed ("1 scoop").
  const typedDose = Boolean(text) && text !== sentence && !instructions;
  return {
    drug: record ? { kind: "typed", name: medicationName(record) } : null,
    strength: null,
    dose: typedDose ? text : doseLabel(record?.dosage?.amount),
    frequency: [frequency?.phrase, asNeeded].filter(Boolean).join(" "),
    instructions,
    stopped:
      stopDateInput(record?.effectivePeriod?.end) ??
      stopDateLabel(record?.effectivePeriod?.end) ??
      (record?.status === "stopped" || record?.status === "completed" ? "unknown" : ""),
  };
}

/** "5 mg Tab" reads "5 mg" in a sentence. */
export const strengthLabel = (strength: RxStrengthOption) => proseDrugName(strength.label);

const AS_NEEDED = /\b(prn|as needed)\b/i;

/**
 * "bid prn" → twice daily, as needed. What is left after "as needed" comes out
 * either names a frequency or is returned unread, to be kept as words.
 */
export function parseFrequency(text: string): { frequency?: DoseFrequency; asNeeded: boolean; unread?: string } {
  const asNeeded = AS_NEEDED.test(text);
  const rest = text.replace(AS_NEEDED, "").replace(/\s+/g, " ").trim().toLowerCase();
  if (!rest) return { asNeeded };
  const frequency = FREQUENCY_CHOICES.find(
    (choice) =>
      choice.keywords.includes(rest) ||
      choice.label.toLowerCase() === rest ||
      FREQUENCY_OPTIONS.find((option) => option.value === choice.value)?.phrase === rest,
  )?.value;
  return frequency ? { frequency, asNeeded } : { asNeeded, unread: text.trim() };
}

/**
 * The phrase being filled: one sentence with a blank per answer. The drug is
 * fixed once the record exists, because a record never changes what it is
 * about; deleting the phrase and adding another is how a wrong drug is fixed.
 */
export function MedicationForm({
  initial,
  drugLocked,
  startAt,
  onDone,
  onRemove,
}: {
  initial: MedicationAnswers;
  drugLocked: boolean;
  /** Where the cursor lands: the first blank for a new phrase, the end when reopened. */
  startAt: "start" | "end";
  /** The phrase was left: by finishing its last blank, Escape, or focus moving out. */
  onDone: (answers: MedicationAnswers) => void;
  /** Backspace past the first blank: the phrase is emptied, so it goes. */
  onRemove: () => void;
}) {
  const [answers, setAnswers] = useState(initial);
  const [drugText, setDrugText] = useState(initial.drug?.name ?? "");
  const [strengthText, setStrengthText] = useState(initial.strength ? strengthLabel(initial.strength) : "");
  const wrapper = useRef<HTMLFieldSetElement>(null);
  const done = useRef(false);
  const set = (changes: Partial<MedicationAnswers>) => setAnswers((current) => ({ ...current, ...changes }));

  const finish = (final = answers) => {
    if (done.current) return;
    done.current = true;
    onDone(final);
  };
  // Leaving the phrase by any route ends it: Tab past the last blank, a click elsewhere in the note.
  const onBlur = (event: FocusEvent) => {
    if (!wrapper.current?.contains(event.relatedTarget as Node | null)) finish();
  };
  const next = (from: HTMLElement | null) => {
    const inputs = Array.from(wrapper.current?.querySelectorAll<HTMLInputElement>("input:not(:disabled)") ?? []);
    const index = inputs.indexOf(from as HTMLInputElement);
    const following = inputs[index + 1];
    if (following) following.focus();
    else finish();
  };
  // Reopening a phrase puts the cursor at its end, as clicking or backspacing into a sentence would.
  useEffect(() => {
    if (startAt !== "end" && !drugLocked) return;
    const inputs = wrapper.current?.querySelectorAll<HTMLInputElement>("input:not(:disabled)");
    inputs?.[inputs.length - 1]?.focus();
  }, [startAt, drugLocked]);

  // Backspace runs back through the blanks the way it runs back through text:
  // an empty blank hands over to the one before it, and past the first the
  // phrase itself goes. Removing one therefore means emptying it first.
  const onKeyDown = (event: KeyboardEvent<HTMLFieldSetElement>) => {
    const input = event.target as HTMLInputElement;
    if (event.key !== "Backspace" || input.tagName !== "INPUT" || input.value !== "") return;
    event.preventDefault();
    const inputs = Array.from(wrapper.current?.querySelectorAll<HTMLInputElement>("input:not(:disabled)") ?? []);
    const previous = inputs[inputs.indexOf(input) - 1];
    if (previous) {
      previous.focus();
      return;
    }
    done.current = true;
    onRemove();
  };
  // After the pick has rendered: picking a drug is what makes its strength blank
  // appear. Held as state so the move runs once React has committed the pick.
  const [advanceFrom, setAdvanceFrom] = useState<HTMLElement | null>(null);
  const advance = () => setAdvanceFrom(document.activeElement as HTMLElement | null);
  useEffect(() => {
    if (!advanceFrom) return;
    setAdvanceFrom(null);
    next(advanceFrom);
  });

  const search = useDrugSearch(answers.drug ? "" : drugText);
  const typed = drugText.trim();
  const drugOptions: PillOption[] = answers.drug
    ? []
    : [
        ...search.suggestions.map((name) => ({ value: `suggest:${name}`, label: name, hint: "did you mean" })),
        ...search.options.map((option) => ({
          value: `drug:${option.name}`,
          label: option.name,
          hint: `${option.strengths.length} ${option.strengths.length === 1 ? "strength" : "strengths"}`,
        })),
        ...(typed.length >= 2 ? [{ value: `typed:${typed}`, label: typed, hint: "as typed" }] : []),
      ];

  const strengths = answers.drug?.kind === "coded" ? answers.drug.strengths : [];
  const strengthOptions: PillOption[] = strengths.length
    ? [
        { value: "unknown", label: "strength unknown" },
        ...strengths.map((strength) => ({
          value: strength.rxcui,
          label: strengthLabel(strength),
          // Two products can share a strength ("200 mg" tablet and capsule); their own labels tell them apart.
          hint:
            strengths.filter((other) => strengthLabel(other) === strengthLabel(strength)).length > 1
              ? strength.label
              : undefined,
        })),
      ].filter((option) => !strengthText || option.label.toLowerCase().startsWith(strengthText.toLowerCase()))
    : [];

  const frequencyQuery = answers.frequency.trim().toLowerCase();
  const asNeededOption: PillOption[] =
    !frequencyQuery || "as needed".startsWith(frequencyQuery) || "prn".startsWith(frequencyQuery)
      ? [{ value: "prn", label: "as needed", hint: "PRN" }]
      : [];
  const frequencyOptions: PillOption[] = [
    ...asNeededOption,
    ...FREQUENCY_CHOICES.filter(
      (choice) =>
        !frequencyQuery ||
        choice.label.toLowerCase().includes(frequencyQuery) ||
        choice.keywords.some((keyword) => keyword.startsWith(frequencyQuery)),
    ).map(
      (choice): PillOption => ({
        value: choice.value,
        label: FREQUENCY_OPTIONS.find((option) => option.value === choice.value)?.phrase ?? choice.label,
        hint: choice.label.match(/\(([A-Z]+)\)/)?.[1],
      }),
    ),
  ];

  const exit = () => finish();

  return (
    <fieldset
      ref={wrapper}
      aria-label="Medication phrase"
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      className="m-0 inline border-0 p-0"
    >
      Pt {isStopped(answers) ? "previously took" : "takes"}{" "}
      {drugLocked ? (
        <input
          disabled
          value={drugText}
          title="The drug can't change once saved. Delete the phrase and add another."
          className={`${PILL_CLASS} ${pillStateClass(false)}`}
        />
      ) : (
        <PillCombobox
          autoFocus={startAt === "start"}
          label="medication"
          text={drugText}
          onText={(text) => {
            setDrugText(text);
            set({ drug: null, strength: null });
            setStrengthText("");
          }}
          options={drugOptions}
          busy={!answers.drug && search.isLoading}
          status={
            answers.drug
              ? undefined
              : typed.length < 2
                ? "Type a medication"
                : search.isLoading
                  ? "Searching…"
                  : "No matches"
          }
          onPick={(option) => {
            const [kind, ...rest] = option.value.split(":");
            const name = rest.join(":");
            if (kind === "suggest") {
              setDrugText(name);
              return false;
            }
            if (kind === "typed") {
              setDrugText(name);
              set({ drug: { kind: "typed", name }, strength: null });
              return undefined;
            }
            const picked = search.options.find((candidate) => candidate.name === name);
            if (!picked) return false;
            setDrugText(proseDrugName(picked.name));
            set({
              drug: {
                kind: "coded",
                name: picked.name,
                route: routeFromForm(picked.name),
                strengths: picked.strengths,
              },
              strength: null,
            });
            return undefined;
          }}
          onAdvance={advance}
          onExit={exit}
        />
      )}
      {strengths.length > 0 && (
        <>
          {" "}
          <PillCombobox
            label="strength"
            text={strengthText}
            onText={setStrengthText}
            options={strengthOptions}
            onPick={(option) => {
              const strength = strengths.find((candidate) => candidate.rxcui === option.value) ?? null;
              setStrengthText(strength ? strengthLabel(strength) : "");
              set({ strength });
              return undefined;
            }}
            onAdvance={advance}
            onExit={exit}
          />
        </>
      )}
      ,{" "}
      <PillCombobox
        label="dose"
        text={answers.dose}
        onText={(dose) => set({ dose })}
        options={[]}
        onPick={() => undefined}
        onAdvance={advance}
        onExit={exit}
      />{" "}
      <PillCombobox
        label="frequency"
        text={answers.frequency}
        onText={(frequency) => set({ frequency })}
        options={frequencyOptions}
        onPick={(option) => {
          set({ frequency: option.label });
          return undefined;
        }}
        onAdvance={advance}
        onExit={exit}
      />
      {answers.instructions ? ", " : " "}
      <PillCombobox
        label="instructions"
        text={answers.instructions}
        onText={(instructions) => set({ instructions })}
        options={[]}
        onPick={() => undefined}
        onAdvance={advance}
        onExit={exit}
      />
      {isStopped(answers) ? ", stopped " : " "}
      <DatePill
        label="stopped"
        text={answers.stopped}
        onText={(stopped) => set({ stopped })}
        onAdvance={advance}
        onExit={exit}
      />
      .
    </fieldset>
  );
}
